/**
 * UserManagement - User Management Dashboard for Admin Portal
 * 
 * Enhanced with customer support capabilities:
 * - Multi-field search (email, name, phone, user ID)
 * - User detail view with comprehensive data
 * - SuperAdmin actions (edit, disable/enable, reset password)
 * - Audit logging for all modifications
 * 
 * Migrated from src/app/components/admin/UserManagement.jsx
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import AdminCache from '../components/shared/AdminCache';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import SearchBar from '../components/common/SearchBar';
import { generateMockUsers, mergeUsers, isMockEnabled } from '../utils/mockUsers';

const { TableSkeleton } = SkeletonLoaderComponents;
import DataTable from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './UserManagement.css';

const USER_MANAGEMENT_URL = API_ENDPOINTS.ADMIN.USER_MANAGEMENT;

const UserManagement = () => {
  const { user: currentUser } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [realUsers, setRealUsers] = useState([]); // Store real users separately
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all'); // NEW: Activity filter
  const [signupProgressFilter, setSignupProgressFilter] = useState('all'); // Signup progress filter
  const [paginationToken, setPaginationToken] = useState(null);

  const isSuperAdmin = currentUser?.isSuperAdmin || false;

  // Generate mock users once (memoized)
  const mockUsers = useMemo(() => {
    if (isMockEnabled()) {
      console.log('[UserManagement] Mock users enabled, generating...');
      return generateMockUsers();
    }
    return [];
  }, []);

  // Use refs to avoid stale closures and dependency loops
  const searchTermRef = useRef(searchTerm);
  const statusFilterRef = useRef(statusFilter);
  const activityFilterRef = useRef(activityFilter); // NEW: Activity filter ref
  const signupProgressFilterRef = useRef(signupProgressFilter);
  const paginationTokenRef = useRef(paginationToken);
  searchTermRef.current = searchTerm;
  statusFilterRef.current = statusFilter;
  activityFilterRef.current = activityFilter; // NEW
  signupProgressFilterRef.current = signupProgressFilter;
  paginationTokenRef.current = paginationToken;

  // Fetch users from API — stable callback (no state in deps)
  const fetchUsers = useCallback(async (resetPagination = false) => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      // Lambda functions need to check groups for admin access
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      if (searchTermRef.current) params.append('search', searchTermRef.current);
      if (statusFilterRef.current && statusFilterRef.current !== 'all') params.append('status', statusFilterRef.current);
      if (!resetPagination && paginationTokenRef.current) params.append('paginationToken', paginationTokenRef.current);

      const response = await fetch(`${USER_MANAGEMENT_URL}/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error('Unauthorized - Admin access required');
        if (response.status === 403) throw new Error('Forbidden - Insufficient permissions');
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        let realUsersList = data.users || [];
        
        // Store real users
        setRealUsers(realUsersList);
        
        // Merge with mock users if enabled
        let allUsers = mergeUsers(realUsersList, mockUsers);
        
        // NEW: Client-side activity filtering
        if (activityFilterRef.current && activityFilterRef.current !== 'all') {
          const now = Date.now();
          const DAY = 86400000; // milliseconds in a day
          
          allUsers = allUsers.filter(user => {
            if (!user.lastLoginAt) {
              return activityFilterRef.current === 'zombie';
            }
            
            const daysSince = (now - new Date(user.lastLoginAt).getTime()) / DAY;
            
            if (activityFilterRef.current === 'active') return daysSince <= 7;
            if (activityFilterRef.current === 'inactive') return daysSince > 7 && daysSince <= 30;
            if (activityFilterRef.current === 'zombie') return daysSince > 30;
            
            return true;
          });
        }
        
        // Client-side signup progress filtering
        if (signupProgressFilterRef.current && signupProgressFilterRef.current !== 'all') {
          allUsers = allUsers.filter(user => {
            if (!user.signupProgress) return false;
            
            if (signupProgressFilterRef.current === 'complete') return user.signupProgress.isComplete;
            if (signupProgressFilterRef.current === 'incomplete') return !user.signupProgress.isComplete;
            return user.signupProgress.stage === signupProgressFilterRef.current;
          });
        }
        
        setUsers(prev => resetPagination ? allUsers : [...prev, ...allUsers]);
        setPaginationToken(data.paginationToken || null);
        
        // Cache the results
        AdminCache.set(AdminCache.KEYS.USER_LIST, allUsers, AdminCache.TTL.medium);
      }
    } catch (err) {
      console.error('[UserManagement] Error:', err);
      setError(err.message);
      
      // Try to load from cache
      const cachedUsers = AdminCache.get(AdminCache.KEYS.USER_LIST);
      if (cachedUsers) {
        setUsers(cachedUsers);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount only
  useEffect(() => {
    fetchUsers(true);
  }, [fetchUsers]);

  // Stable search handler — won't cause SearchBar useEffect to re-fire
  const handleSearch = useCallback((value, filters) => {
    searchTermRef.current = value;
    statusFilterRef.current = filters.status || 'all';
    activityFilterRef.current = filters.activity || 'all'; // NEW: Handle activity filter
    signupProgressFilterRef.current = filters.signupProgress || 'all';
    setSearchTerm(value);
    setStatusFilter(filters.status || 'all');
    setActivityFilter(filters.activity || 'all'); // NEW
    setSignupProgressFilter(filters.signupProgress || 'all');
    fetchUsers(true);
  }, [fetchUsers]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get activity status badge config
  const getActivityConfig = (lastLoginAt) => {
    if (!lastLoginAt) {
      return { text: 'Never', variant: 'danger', icon: '🔴' };
    }
    
    const daysSince = (Date.now() - new Date(lastLoginAt).getTime()) / 86400000;
    
    if (daysSince <= 7) {
      return { text: 'Active', variant: 'success', icon: '🟢' };
    } else if (daysSince <= 30) {
      return { text: 'Inactive', variant: 'warning', icon: '🟡' };
    } else {
      return { text: 'Zombie', variant: 'danger', icon: '🔴' };
    }
  };

  // Get status badge config
  const getStatusConfig = (status, enabled) => {
    if (!enabled) return { text: 'Disabled', variant: 'danger' };
    
    const statusMap = {
      'CONFIRMED': { text: 'Active', variant: 'success' },
      'UNCONFIRMED': { text: 'Unconfirmed', variant: 'warning' },
      'FORCE_CHANGE_PASSWORD': { text: 'Password Reset', variant: 'info' },
      'RESET_REQUIRED': { text: 'Reset Required', variant: 'warning' }
    };
    
    return statusMap[status] || { text: status, variant: 'default' };
  };

  // Get signup progress badge config
  const getSignupProgressConfig = (signupProgress) => {
    if (!signupProgress) return { text: 'Unknown', variant: 'default', icon: '❓' };
    
    const configs = {
      'email_pending': { text: 'Email Pending', variant: 'danger', icon: '📧' },
      'payment_pending': { text: 'Payment Pending', variant: 'warning', icon: '💳' },
      'student_pending': { text: 'No Student', variant: 'warning', icon: '👤' },
      'complete': { text: 'Complete', variant: 'success', icon: '✅' }
    };
    
    return configs[signupProgress.stage] || { text: signupProgress.stageLabel, variant: 'default', icon: '❓' };
  };

  // Table columns configuration
  const columns = [
    {
      key: 'user',
      label: 'User',
      sortable: false,
      render: (user) => (
        <div className="user-management__user-cell">
          <div className="user-management__avatar">
            {(user.name || user.email || '?')[0].toUpperCase()}
          </div>
          <div className="user-management__user-info">
            <div className="user-management__user-name">{user.name || 'No name'}</div>
            <div className="user-management__user-email">{user.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (user) => {
        const config = getStatusConfig(user.status, user.enabled);
        return <StatusBadge text={config.text} variant={config.variant} />;
      }
    },
    {
      key: 'activity',
      label: 'Activity',
      render: (user) => {
        const config = getActivityConfig(user.lastLoginAt);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>{config.icon}</span>
            <StatusBadge text={config.text} variant={config.variant} />
          </div>
        );
      }
    },
    {
      key: 'lastLoginAt',
      label: 'Last Login',
      render: (user) => user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'
    },
    {
      key: 'signupProgress',
      label: 'Signup Progress',
      render: (user) => {
        const config = getSignupProgressConfig(user.signupProgress);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>{config.icon}</span>
            <StatusBadge text={config.text} variant={config.variant} />
            {user.signupProgress && !user.signupProgress.isComplete && (
              <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                {user.signupProgress.requiredCompleted}/{user.signupProgress.requiredTotal}
              </span>
            )}
            {user.signupProgress?.hasAttributeMismatches && (
              <span title="Attribute mismatches detected" style={{ cursor: 'help' }}>🔧</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'groups',
      label: 'Groups',
      sortable: false,
      render: (user) => (
        <div className="user-management__groups">
          {user.groups?.length > 0 ? (
            user.groups.map(g => (
              <span 
                key={g} 
                className={`user-management__group-badge ${
                  g === 'SuperAdmins' ? 'user-management__group-badge--super' : 
                  g === 'Admins' ? 'user-management__group-badge--admin' : ''
                }`}
              >
                {g}
              </span>
            ))
          ) : (
            <span className="user-management__no-groups">-</span>
          )}
        </div>
      )
    },
    {
      key: 'createdAt',
      label: 'Sign-up Date',
      render: (user) => formatDate(user.createdAt)
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (user) => (
        <div className="user-management__actions">
          <Link to={`/users/${user.userId}`} className="user-management__action-btn user-management__action-btn--view">
            View
          </Link>
        </div>
      )
    }
  ];

  // Search filters
  const searchFilters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'CONFIRMED', label: 'Active' },
        { value: 'UNCONFIRMED', label: 'Unconfirmed' },
        { value: 'FORCE_CHANGE_PASSWORD', label: 'Password Reset' }
      ]
    },
    {
      key: 'activity',
      label: 'Activity',
      options: [
        { value: 'all', label: 'All Activity' },
        { value: 'active', label: '🟢 Active (7 days)' },
        { value: 'inactive', label: '🟡 Inactive (8-30 days)' },
        { value: 'zombie', label: '🔴 Zombie (>30 days)' }
      ]
    },
    {
      key: 'signupProgress',
      label: 'Signup Progress',
      options: [
        { value: 'all', label: 'All Progress' },
        { value: 'complete', label: '✅ Complete' },
        { value: 'incomplete', label: '⚠️ Incomplete' },
        { value: 'email_pending', label: '📧 Email Pending' },
        { value: 'payment_pending', label: '💳 Payment Pending' },
        { value: 'student_pending', label: '👤 No Student Account' }
      ]
    }
  ];

  if (loading && users.length === 0) {
    return <TableSkeleton rows={8} columns={6} />;
  }

  return (
    <div className="user-management">
      {/* Header */}
      <div className="user-management__header">
        <h2 className="user-management__title">User Management</h2>
        <div className="user-management__header-badges">
          {isMockEnabled() && (
            <span className="user-management__mock-badge" title="Demo mode: Showing mock users for panel demo">
              🎭 Demo Mode ({mockUsers.length} mock users)
            </span>
          )}
          {isSuperAdmin && (
            <span className="user-management__super-badge">🔐 SuperAdmin</span>
          )}
        </div>
      </div>

      {error && (
        <div className="admin-alert admin-alert--warning">
          <span>⚠️</span> {error}
        </div>
      )}

      {/* Search */}
      <div className="admin-card">
        <SearchBar
          placeholder="Search by email, name, phone, or user ID..."
          onSearch={handleSearch}
          filters={searchFilters}
          debounceMs={500}
        />
      </div>

      {/* User Table */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Users ({users.length})</h3>
        </div>
        <DataTable
          columns={columns}
          data={users}
          loading={loading}
          emptyMessage="No users found"
        />
        {paginationToken && (
          <div className="user-management__pagination">
            <button
              onClick={() => fetchUsers(false)}
              className="user-management__load-more"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
